import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useManufacturerSearch } from "@/hooks/useManufacturerSearch";

interface ManufacturerOption {
  label: string;
  value: string;
}

interface MobileManufacturerSearchProps {
  manufacturers: ManufacturerOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function MobileManufacturerSearch({
  manufacturers,
  value,
  onChange,
  disabled = false,
  className
}: MobileManufacturerSearchProps) {
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use optimized search hook with indexing
  const filteredManufacturers = useManufacturerSearch(manufacturers, searchValue, 3);

  const selectedManufacturer = manufacturers.find((m) => m.value === value);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSuggestions]);

  const handleSelect = (manufacturer: ManufacturerOption) => {
    onChange(manufacturer.value);
    setSearchValue("");
    setShowSuggestions(false);
  };

  const handleClear = () => {
    onChange("");
    setSearchValue("");
    setShowSuggestions(false);
  };

  return (
    <div ref={containerRef} className={cn("relative z-[2000]", className)}>
      {selectedManufacturer ? (
        <div className="flex gap-2 h-12">
          <div className="flex-1 flex items-center px-4 rounded-md border-2 bg-muted/50 border-primary/20">
            <span className="font-mono text-base font-medium">{selectedManufacturer.label}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12"
            onClick={handleClear}
            disabled={disabled}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
            <Input
              type="text"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Rechercher une marque..."
              className="pl-10 h-12 text-base"
              disabled={disabled}
            />
          </div>

          {showSuggestions && filteredManufacturers.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border-2 rounded-lg shadow-xl z-[9999] max-h-[30vh] overflow-y-auto">
              {filteredManufacturers.map((manufacturer) => (
                <button
                  key={manufacturer.value}
                  type="button"
                  onClick={() => handleSelect(manufacturer)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 active:bg-muted transition-colors border-b last:border-b-0 min-h-[3rem] flex items-center"
                >
                  <span className="font-mono text-base">{manufacturer.label}</span>
                </button>
              ))}
            </div>
          )}

          {showSuggestions && searchValue && filteredManufacturers.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border-2 rounded-lg shadow-xl z-[9999] px-4 py-3">
              <p className="text-sm text-muted-foreground">Aucune marque trouvée</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
